import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input-field',
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true,
    },
  ],
})
export class InputFieldComponent implements ControlValueAccessor{
  @Input() label: string = '';
  @Input() value: any = '';
  @Input() disabled: boolean = false;
  @Input() type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'decimal' | 'textarea' = 'text';
  @Input() required: boolean = false;
  @Input() cssClass: string = '';
  @Input() outlined: boolean = true;
  @Input() rows: number = 3;
  @Input() placeholder: string = '';

  @Output() valueChange = new EventEmitter<any>();
  @Output() onEmit = new EventEmitter<any>();

  // Email
  @Input() emailPattern: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  @Output() emailValidityChange = new EventEmitter<boolean>();
  emailValid: boolean = true;

  // Number
  @Input() numberPattern: RegExp = /^[0-9]+(\.[0-9]+)?$/;
  @Input() integerOnly: boolean = false;
  @Input() min?: number;
  @Input() max?: number;
  @Output() numberValidityChange = new EventEmitter<boolean>();
  numberValid: boolean = true;

  //decimal
  @Input() decimalPattern: RegExp = /^[0-9]+(\.[0-9]{1,2})?$/;
  @Input() decimalMin?: number;
  @Input() decimalMax?: number;
  @Output() decimalValidityChange = new EventEmitter<boolean>();
  decimalValid: boolean = true;

  // Tel
  @Input() telPattern: RegExp = /^[0-9()+\-.\s]{7,20}$/;
  @Output() telValidityChange = new EventEmitter<boolean>();
  telValid: boolean = true;

  //password
  @Input() passwordPattern: RegExp = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
  @Output() passwordValidityChange = new EventEmitter<boolean>();
  passwordValid: boolean = true;

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private changeDetector: ChangeDetectorRef) {}

  writeValue(val: any): void {
    this.value = val ?? '';
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; this.changeDetector.markForCheck(); }

  handleBlur(): void {
    this.onTouched();
  }
  
  handleChange(raw: any): void {
    let inputValue = raw?.target ? raw.target.value : raw;

    // Number
    if (this.type === 'number') {
      const rawInput = inputValue;
      if (typeof inputValue === 'string') {
        inputValue = inputValue.trim();
        if (this.integerOnly) {
          inputValue = inputValue.replace(/[^0-9]/g, '');
        }
      }
      const isEmpty = inputValue === '' || inputValue == null;
      const matchesPattern = isEmpty ? !this.required : this.numberPattern.test(String(inputValue));
      let parsedNumber: number | null = matchesPattern && !isEmpty ? Number(inputValue) : null;
      let withinRange = true;
      if (parsedNumber !== null) {
        if (this.min !== undefined && parsedNumber < this.min) withinRange = false;
        if (this.max !== undefined && parsedNumber > this.max) withinRange = false;
      }
      const prevValid = this.numberValid;
      this.numberValid = matchesPattern && withinRange;
      if (prevValid !== this.numberValid) this.numberValidityChange.emit(this.numberValid);
      inputValue = (this.numberValid && parsedNumber !== null) ? parsedNumber : rawInput;
    }

    if (this.type === 'decimal') {
      const rawInput = inputValue;
      if (typeof inputValue === 'string') {
        inputValue = inputValue.trim();
      }
      const isEmpty = inputValue === '' || inputValue == null;
      const matchesPattern = isEmpty ? !this.required : this.decimalPattern.test(String(inputValue));
      let parsedDecimal: number | null = matchesPattern && !isEmpty ? Number(inputValue) : null;
      let withinRange = true;
      if (parsedDecimal !== null) {
        if (this.decimalMin !== undefined && parsedDecimal < this.decimalMin) withinRange = false;
        if (this.decimalMax !== undefined && parsedDecimal > this.decimalMax) withinRange = false;
        // Normalize to 2 decimal places (keeps numeric model consistent)
        parsedDecimal = Number(parsedDecimal.toFixed(2));
      }
      const prevValid = this.decimalValid;
      this.decimalValid = matchesPattern && withinRange;
      if (prevValid !== this.decimalValid) this.decimalValidityChange.emit(this.decimalValid);
      inputValue = (this.decimalValid && parsedDecimal !== null) ? parsedDecimal : rawInput;
    }
    
    // Email
    if (this.type === 'email') {
      const prevEmail = this.emailValid;
      const empty = inputValue === '' || inputValue == null;
      this.emailValid = this.emailPattern.test(String(inputValue)) || (!this.required && empty);
      if (prevEmail !== this.emailValid) this.emailValidityChange.emit(this.emailValid);
    }

    // Tel
    if (this.type === 'tel') {
      const prevTel = this.telValid;
      const empty = inputValue === '' || inputValue == null;
      this.telValid = this.telPattern.test(String(inputValue)) || (!this.required && empty);
      if (prevTel !== this.telValid) this.telValidityChange.emit(this.telValid);
    }

    // Password
    if (this.type === 'password') {
      const prevPassword = this.passwordValid;
      const empty = inputValue === '' || inputValue == null;
      this.passwordValid = this.passwordPattern.test(String(inputValue)) || (!this.required && empty);
      if (prevPassword !== this.passwordValid) this.passwordValidityChange.emit(this.passwordValid);
    }

    if (inputValue !== this.value) {
      this.value = inputValue;
      this.onChange(inputValue);
      this.valueChange.emit(inputValue);
      this.onEmit.emit(inputValue);
      this.changeDetector.markForCheck();
    }
  }
}